import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { VehicleSelectionOverviewComponentState } from '@components/selection-overview/components/vehicle-selection-overview/vehicle-selection-overview.component';
import { BladeHeaderComponent } from '@modules/blades/components/blade-header/blade-header.component';
import { Blade, IBladeComponent } from '@modules/blades/models/blade-component';
import { SelectedVehiclesDictionary } from '@modules/core/models/custom-dictionary';
import { VehicleStore } from '@modules/core/store';
import { CompactListComponent, SpinnerComponent } from '@modules/shared/components';
import { ColumnFactory } from '@modules/shared/helpers/column-factory';
import { CompactListColumn, CompactListItem } from '@modules/shared/models';
import _ from 'lodash';
import { BehaviorSubject, map, Observable, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-selected-vehicle',
    templateUrl: 'selected-vehicle.component.html',
    styleUrls: ['./selected-vehicle.component.scss'],
    imports: [
        CompactListComponent,
        SpinnerComponent,
        AsyncPipe,
        BladeHeaderComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
@Blade({
  name: 'SelectedVehicleComponent'
})
export class SelectedVehicleComponent implements OnInit, OnDestroy, IBladeComponent {
  bladeIndex: number;
  childComponent?: IBladeComponent;
  title?: string;
  isLoading$?: Observable<boolean>;
  isLoadingExtra$?: Observable<boolean>;

  public isLoading = false;
  public items: CompactListItem[];
  public columns: CompactListColumn[];

  private _stateKey = 'selected-vehicle-component-state';
  private readonly _router = inject(Router);
  private readonly _vehicleStore = inject(VehicleStore);

  public title$: Observable<string|undefined>;
  private _title$: BehaviorSubject<string> = new BehaviorSubject(undefined);

  private _destroy$ = new Subject<void>();
  private _selectedVehicles: SelectedVehiclesDictionary;

  constructor() {
    this.title$ = this._title$.asObservable();
    this._initState();
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return true;
  }

  ngOnInit() { 
    this._vehicleStore.getVehiclesState().pipe(
        map(vehicles => {
            if (!vehicles) {
                // needed for the spinner to show instead of empty data-feedback
                return null;
            } else {
                return _.map(vehicles, vehicle => {
                    const item = new CompactListItem();
                    item.id = vehicle.inventoryNumber;
                    item.type = 'vehicle';
                    item.properties['inventoryNumber'] = vehicle.inventoryNumber;
                    item.properties['inventoryNumberWithoutPrefix'] = vehicle.inventoryNumberWithoutPrefix;
                    item.properties['garageCode'] = vehicle.garageCode;
                    item.properties['speed'] = vehicle.position?.speed;
                    item.properties['bearing'] = vehicle.position?.bearing;
                    item.properties['tripNumber'] = vehicle.tripNumber;
                    item.properties['blockNumber'] = vehicle.blockNumber;
                    item.properties['routeNumber'] = vehicle.routeNumber;
                    item.properties['warnings'] = vehicle.displayableWarnings?.length ?? 0;
                    item.properties['stateOfCharge'] = vehicle.stateOfCharge ? vehicle.stateOfCharge.sensorValue + '%' : '';
                    item.properties['onTrip'] = vehicle.onTrip;
                    item.properties['onVehicleTaskBlock'] = vehicle.onVehicleTaskBlock;
                    if (this._selectedVehicles) {
                        item.selected = _.includes(this._selectedVehicles.allValues, item.id);
                    }
                    return item;
                });
            }
        }),
        takeUntil(this._destroy$)
    ).subscribe((items) => {
        this.items = items;
    });
  }

  public columnsUpdated(columns: CompactListColumn[]) {
    this._updateState();
  }

  private _initState() {
    this._initColumnDefaults();

    const stateData = localStorage.getItem(this._stateKey);
    if (stateData) {
        const state = Object.assign(new VehicleSelectionOverviewComponentState(), JSON.parse(stateData));
        this.columns = _.map(this.columns, (column) => {
            const storedColumn = _.find(state.columns, (c) => c.key === column.key);
            if (storedColumn) {
                delete column.sort;
                delete storedColumn.width;
                delete storedColumn.sortNumeric;
                delete storedColumn.icon;
            }
            return { ...column, ...storedColumn };
        });
    }
  }

  private _updateState() {
    let state = new VehicleSelectionOverviewComponentState();
    state = {
        ...state,
        columns: this.columns
    };
    localStorage.setItem(this._stateKey, JSON.stringify(state));
  }

  private _initColumnDefaults() {
    this.columns = [
        ColumnFactory.getInventoryColumn({ sort: 'asc' }),
        ColumnFactory.getRouteNumberColumn(),
        ColumnFactory.getTripNumberColumn(),
        ColumnFactory.getBlockNumberColumn(),
        ColumnFactory.getSpeedColumn(),
        ColumnFactory.getBearingColumn(),
        ColumnFactory.getWarningColumn({ visible: false }),
        ColumnFactory.getStateOfChargeColumm({ visible: false })
    ];
  }

  itemSelected(item: any): void {
    const encodedItemId = encodeURIComponent(item.id);
    const indexOfItemId = this._router.routerState.snapshot.url.indexOf(`outlet-right0:vehicle/${encodedItemId}`);
    if (indexOfItemId === -1) {
        this._router.navigate(['', { outlets: { 'outlet-right0': ['vehicle', item.id], 'outlet-right1': null, 'outlet-right2': null }}]);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}